'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { penNameToSlug } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Comment {
  id: string;
  article_slug: string;
  parent_id: string | null;
  author_name: string;
  body: string;
  upvotes: number;
  is_flagged: boolean;
  flag_count: number;
  created_at: string;
  replies?: Comment[];
}

interface ArticleCommentsProps {
  articleSlug: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getVoterKey(): string {
  if (typeof window === 'undefined') return '';
  let key = localStorage.getItem('smi_voter_key');
  if (!key) {
    key = `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('smi_voter_key', key);
  }
  return key;
}

function getUpvotedSet(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem('smi_upvoted_comments');
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveUpvotedSet(set: Set<string>) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('smi_upvoted_comments', JSON.stringify([...set]));
}

function getFlaggedSet(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem('smi_flagged_comments');
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveFlaggedSet(set: Set<string>) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('smi_flagged_comments', JSON.stringify([...set]));
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function nestComments(flat: Comment[]): Comment[] {
  const map = new Map<string, Comment>();
  const roots: Comment[] = [];
  flat.forEach((c) => map.set(c.id, { ...c, replies: [] }));
  map.forEach((c) => {
    if (c.parent_id && map.has(c.parent_id)) {
      map.get(c.parent_id)!.replies!.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots;
}

async function supabasePost(path: string, body: object): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1${path}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      return { ok: false, error: text };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Request failed' };
  }
}

// ─── Comment Form ─────────────────────────────────────────────────────────────

interface CommentFormProps {
  articleSlug: string;
  parentId?: string | null;
  onSubmitted: () => void;
  onCancel?: () => void;
  placeholder?: string;
  compact?: boolean;
}

function CommentForm({ articleSlug, parentId = null, onSubmitted, onCancel, placeholder, compact }: CommentFormProps) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (compact && textareaRef.current) textareaRef.current.focus();
  }, [compact]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) { setError('Comment cannot be empty.'); return; }
    if (!name.trim()) { setError('Please enter your name.'); return; }
    setError('');
    setSubmitting(true);
    const { ok, error: err } = await supabasePost('/smi_article_comments', {
      article_slug: articleSlug,
      article_id: articleSlug,
      parent_id: parentId || null,
      author_name: name.trim(),
      body: body.trim(),
    });
    setSubmitting(false);
    if (!ok) {
      setError(err || 'Failed to post comment. Please try again.');
      return;
    }
    setName('');
    setBody('');
    onSubmitted();
  }

  return (
    <form data-component-id="src/app/article/[slug]/ArticleComments.tsx:167:4" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="167" data-component-end-line="205" data-component-file="ArticleComments.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-3%22%7D" onSubmit={handleSubmit} className="space-y-3">
      <input data-component-id="src/app/article/[slug]/ArticleComments.tsx:168:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="168" data-component-end-line="175" data-component-file="ArticleComments.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Aname%5D%22%2C%22className%22%3A%22w-full%20bg-%5B%230d1420%5D%20border%20border-white%2F10%20rounded%20px-3%20py-2%20text-sm%20text-white%20placeholder-white%2F30%20focus%3Aoutline-none%20focus%3Aborder-%5B%230ea5e9%5D%2F50%20transition-colors%22%7D"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        maxLength={80}
        className="w-full bg-[#0d1420] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#0ea5e9]/50 transition-colors"
      />
      <textarea data-component-id="src/app/article/[slug]/ArticleComments.tsx:176:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="176" data-component-end-line="184" data-component-file="ArticleComments.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22value%22%3A%22%5Bvar%3Abody%5D%22%2C%22className%22%3A%22w-full%20bg-%5B%230d1420%5D%20border%20border-white%2F10%20rounded%20px-3%20py-2%20text-sm%20text-white%20placeholder-white%2F30%20focus%3Aoutline-none%20focus%3Aborder-%5B%230ea5e9%5D%2F50%20transition-colors%20resize-none%22%7D"
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder || 'Share your thoughts on this story…'}
        rows={compact ? 3 : 4}
        maxLength={2000}
        className="w-full bg-[#0d1420] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#0ea5e9]/50 transition-colors resize-none"
      />
      {error && <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:185:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="185" data-component-end-line="185" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-red-400%20text-xs%22%7D" className="text-red-400 text-xs">{error}</p>}
      <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:186:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="186" data-component-end-line="204" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
        <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:187:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="187" data-component-end-line="193" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22bg-%5B%230ea5e9%5D%20hover%3Abg-%5B%230284c7%5D%20disabled%3Aopacity-50%20text-white%20text-xs%20font-bold%20px-4%20py-2%20rounded%20transition-colors%20uppercase%20tracking-wider%22%7D"
          type="submit"
          disabled={submitting}
          className="bg-[#0ea5e9] hover:bg-[#0284c7] disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded transition-colors uppercase tracking-wider"
        >
          {submitting ? 'Posting…' : compact ? 'Reply' : 'Post Comment'}
        </button>
        {onCancel && (
          <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:195:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="195" data-component-end-line="201" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-white%2F40%20hover%3Atext-white%2F70%20text-xs%20px-3%20py-2%20transition-colors%22%2C%22textContent%22%3A%22Cancel%22%7D"
            type="button"
            onClick={onCancel}
            className="text-white/40 hover:text-white/70 text-xs px-3 py-2 transition-colors"
          >
            Cancel
          </button>
        )}
        <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:203:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="203" data-component-end-line="203" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-auto%20text-white%2F20%20text-%5B10px%5D%22%2C%22textContent%22%3A%22%2F2000%22%7D" className="ml-auto text-white/20 text-[10px]">{body.length}/2000</span>
      </div>
    </form>
  );
}

// ─── Single Comment ───────────────────────────────────────────────────────────

interface CommentItemProps {
  comment: Comment;
  articleSlug: string;
  depth: number;
  upvotedSet: Set<string>;
  flaggedSet: Set<string>;
  onUpvote: (id: string) => void;
  onFlag: (id: string) => void;
  onReplyPosted: () => void;
}

function CommentItem({
  comment,
  articleSlug,
  depth,
  upvotedSet,
  flaggedSet,
  onUpvote,
  onFlag,
  onReplyPosted,
}: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const hasReplies = (comment.replies?.length ?? 0) > 0;
  const isUpvoted = upvotedSet.has(comment.id);
  const isFlagged = flaggedSet.has(comment.id);
  const maxDepth = 3;

  const initials = comment.author_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:247:4" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="247" data-component-end-line="375" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${depth > 0 ? 'ml-6 pl-4 border-l border-white/8' : ''}`}>
      <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:248:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="248" data-component-end-line="338" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20py-4%22%7D" className="group py-4">
        {/* Author row */}
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:250:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="250" data-component-end-line="337" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
          {/* Avatar */}
          <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:252:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="252" data-component-end-line="254" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20rounded-full%20bg-%5B%230ea5e9%5D%2F10%20border%20border-%5B%230ea5e9%5D%2F20%20flex%20items-center%20justify-center%20flex-shrink-0%20mt-0.5%22%7D" className="w-8 h-8 rounded-full bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:253:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="253" data-component-end-line="253" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%230ea5e9%5D%20font-bold%20text-%5B10px%5D%22%7D" className="text-[#0ea5e9] font-bold text-[10px]">{initials}</span>
          </div>

          <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:256:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="256" data-component-end-line="336" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
            {/* Meta */}
            <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:258:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="258" data-component-end-line="267" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-1.5%20flex-wrap%22%7D" className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Link data-component-id="src/app/article/[slug]/ArticleComments.tsx:259:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="259" data-component-end-line="264" data-component-file="ArticleComments.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-white%2F90%20text-sm%20font-semibold%20hover%3Atext-%5B%230ea5e9%5D%20transition-colors%22%7D"
                href={`/author/${penNameToSlug(comment.author_name)}`}
                className="text-white/90 text-sm font-semibold hover:text-[#0ea5e9] transition-colors"
              >
                {comment.author_name}
              </Link>
              <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:265:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="265" data-component-end-line="265" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%2F20%20text-xs%22%2C%22textContent%22%3A%22%C2%B7%22%7D" className="text-white/20 text-xs">·</span>
              <time data-component-id="src/app/article/[slug]/ArticleComments.tsx:266:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="266" data-component-end-line="266" data-component-file="ArticleComments.tsx" data-component-name="time" data-component-content="%7B%22elementName%22%3A%22time%22%2C%22className%22%3A%22text-white%2F30%20text-xs%22%7D" className="text-white/30 text-xs">{formatRelativeTime(comment.created_at)}</time>
            </div>

            {/* Body */}
            <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:270:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="270" data-component-end-line="272" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F70%20text-sm%20leading-relaxed%20whitespace-pre-wrap%20break-words%22%7D" className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap break-words">
              {comment.body}
            </p>

            {/* Actions */}
            <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:275:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="275" data-component-end-line="318" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%20mt-2.5%22%7D" className="flex items-center gap-4 mt-2.5">
              {/* Upvote */}
              <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:277:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="277" data-component-end-line="291" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => onUpvote(comment.id)}
                disabled={isUpvoted}
                className={`flex items-center gap-1.5 text-xs transition-colors ${
                  isUpvoted
                    ? 'text-[#0ea5e9] cursor-default'
                    : 'text-white/30 hover:text-[#0ea5e9]'
                }`}
                title={isUpvoted ? 'Already upvoted' : 'Upvote this comment'}
              >
                <svg data-component-id="src/app/article/[slug]/ArticleComments.tsx:287:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="287" data-component-end-line="289" data-component-file="ArticleComments.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3.5%20h-3.5%22%7D" className="w-3.5 h-3.5" fill={isUpvoted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path data-component-id="src/app/article/[slug]/ArticleComments.tsx:288:18" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="288" data-component-end-line="288" data-component-file="ArticleComments.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
                <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:290:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="290" data-component-end-line="290" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{comment.upvotes}</span>
              </button>

              {/* Reply */}
              {depth < maxDepth && (
                <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:295:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="295" data-component-end-line="300" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-white%2F30%20hover%3Atext-white%2F70%20text-xs%20transition-colors%22%7D"
                  onClick={() => setShowReplyForm((v) => !v)}
                  className="text-white/30 hover:text-white/70 text-xs transition-colors"
                >
                  {showReplyForm ? 'Cancel' : 'Reply'}
                </button>
              )}

              {/* Flag */}
              <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:304:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="304" data-component-end-line="317" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => onFlag(comment.id)}
                disabled={isFlagged}
                className={`flex items-center gap-1 text-xs transition-colors ml-auto ${
                  isFlagged
                    ? 'text-amber-500/60 cursor-default' :'text-white/15 hover:text-amber-500/60'
                }`}
                title={isFlagged ? 'Already flagged' : 'Flag as inappropriate'}
              >
                <svg data-component-id="src/app/article/[slug]/ArticleComments.tsx:313:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="313" data-component-end-line="315" data-component-file="ArticleComments.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3%20h-3%22%7D" className="w-3 h-3" fill={isFlagged ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path data-component-id="src/app/article/[slug]/ArticleComments.tsx:314:18" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="314" data-component-end-line="314" data-component-file="ArticleComments.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" d="M3 3v18M3 6l9-3 9 3v9l-9 3-9-3V6z" />
                </svg>
                {isFlagged && <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:316:30" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="316" data-component-end-line="316" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Flagged%22%7D">Flagged</span>}
              </button>
            </div>

            {/* Reply form */}
            {showReplyForm && (
              <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:322:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="322" data-component-end-line="334" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%22%7D" className="mt-3">
                <CommentForm data-component-id="src/app/article/[slug]/ArticleComments.tsx:323:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="323" data-component-end-line="333" data-component-file="ArticleComments.tsx" data-component-name="CommentForm" data-component-content="%7B%22elementName%22%3A%22CommentForm%22%7D"
                  articleSlug={articleSlug}
                  parentId={comment.id}
                  compact
                  placeholder={`Reply to ${comment.author_name}…`}
                  onSubmitted={() => {
                    setShowReplyForm(false);
                    onReplyPosted();
                  }}
                  onCancel={() => setShowReplyForm(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested replies */}
      {hasReplies && (
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:342:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="342" data-component-end-line="373" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          {showReplies ? (
            <>
              {comment.replies!.map((reply) => (
                <CommentItem data-component-id="src/app/article/[slug]/ArticleComments.tsx:346:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="346" data-component-end-line="356" data-component-file="ArticleComments.tsx" data-component-name="CommentItem" data-component-content="%7B%22elementName%22%3A%22CommentItem%22%7D"
                  key={reply.id}
                  comment={reply}
                  articleSlug={articleSlug}
                  depth={depth + 1}
                  upvotedSet={upvotedSet}
                  flaggedSet={flaggedSet}
                  onUpvote={onUpvote}
                  onFlag={onFlag}
                  onReplyPosted={onReplyPosted}
                />
              ))}
              <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:358:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="358" data-component-end-line="363" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22ml-6%20text-white%2F25%20hover%3Atext-white%2F50%20text-xs%20py-1%20transition-colors%22%2C%22textContent%22%3A%22%E2%86%91%20Hide%22%7D"
                onClick={() => setShowReplies(false)}
                className="ml-6 text-white/25 hover:text-white/50 text-xs py-1 transition-colors"
              >
                ↑ Hide {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </button>
            </>
          ) : (
            <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:366:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="366" data-component-end-line="371" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22ml-6%20text-%5B%230ea5e9%5D%2F60%20hover%3Atext-%5B%230ea5e9%5D%20text-xs%20py-1%20transition-colors%22%2C%22textContent%22%3A%22%E2%86%93%20Show%22%7D"
              onClick={() => setShowReplies(true)}
              className="ml-6 text-[#0ea5e9]/60 hover:text-[#0ea5e9] text-xs py-1 transition-colors"
            >
              ↓ Show {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ArticleComments({ articleSlug }: ArticleCommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upvotedSet, setUpvotedSet] = useState<Set<string>>(new Set());
  const [flaggedSet, setFlaggedSet] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'newest' | 'top'>('newest');

  useEffect(() => {
    setUpvotedSet(getUpvotedSet());
    setFlaggedSet(getFlaggedSet());
  }, []);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const orderParam = sortBy === 'top' ? 'upvotes.desc,created_at.desc' : 'created_at.asc';
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/smi_article_comments?article_slug=eq.${encodeURIComponent(articleSlug)}&is_approved=eq.true&order=${orderParam}&select=id,article_slug,parent_id,author_name,body,upvotes,is_flagged,flag_count,created_at`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          cache: 'no-store',
        }
      );
      if (!res.ok) throw new Error('Failed to load comments');
      const data: Comment[] = await res.json();
      setComments(nestComments(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [articleSlug, sortBy]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  async function handleUpvote(commentId: string) {
    if (upvotedSet.has(commentId)) return;
    const voterKey = getVoterKey();
    const { ok } = await supabasePost('/smi_comment_upvotes', {
      comment_id: commentId,
      voter_key: voterKey,
    });
    if (!ok) return;
    // Increment upvote count via PATCH
    try {
      await fetch(
        `${SUPABASE_URL}/rest/v1/smi_article_comments?id=eq.${commentId}`,
        {
          method: 'PATCH',
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({ upvotes: (comments.flat().find((c) => c.id === commentId)?.upvotes ?? 0) + 1 }),
        }
      );
    } catch { /* silent */ }
    const newSet = new Set(upvotedSet);
    newSet.add(commentId);
    setUpvotedSet(newSet);
    saveUpvotedSet(newSet);
    // Optimistic update
    setComments((prev) => updateUpvoteInTree(prev, commentId));
  }

  function updateUpvoteInTree(nodes: Comment[], id: string): Comment[] {
    return nodes.map((c) => {
      if (c.id === id) return { ...c, upvotes: c.upvotes + 1 };
      if (c.replies?.length) return { ...c, replies: updateUpvoteInTree(c.replies, id) };
      return c;
    });
  }

  async function handleFlag(commentId: string) {
    if (flaggedSet.has(commentId)) return;
    const voterKey = getVoterKey();
    const { ok } = await supabasePost('/smi_comment_flags', {
      comment_id: commentId,
      flagger_key: voterKey,
      reason: 'inappropriate',
    });
    if (!ok) return;
    // Increment flag count
    try {
      const current = findCommentInTree(comments, commentId);
      await fetch(
        `${SUPABASE_URL}/rest/v1/smi_article_comments?id=eq.${commentId}`,
        {
          method: 'PATCH',
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({ flag_count: (current?.flag_count ?? 0) + 1 }),
        }
      );
    } catch { /* silent */ }
    const newSet = new Set(flaggedSet);
    newSet.add(commentId);
    setFlaggedSet(newSet);
    saveFlaggedSet(newSet);
  }

  function findCommentInTree(nodes: Comment[], id: string): Comment | null {
    for (const c of nodes) {
      if (c.id === id) return c;
      if (c.replies?.length) {
        const found = findCommentInTree(c.replies, id);
        if (found) return found;
      }
    }
    return null;
  }

  const totalCount = countAll(comments);

  function countAll(nodes: Comment[]): number {
    return nodes.reduce((acc, c) => acc + 1 + countAll(c.replies ?? []), 0);
  }

  return (
    <section data-component-id="src/app/article/[slug]/ArticleComments.tsx:513:4" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="513" data-component-end-line="610" data-component-file="ArticleComments.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22mt-10%20pt-8%20border-t%20border-white%2F10%22%2C%22id%22%3A%22comments%22%7D" className="mt-10 pt-8 border-t border-white/10" id="comments" aria-label="Comments">
      {/* Header */}
      <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:515:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="515" data-component-end-line="547" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-6%20flex-wrap%20gap-3%22%7D" className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:516:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="516" data-component-end-line="527" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
          <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:517:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="517" data-component-end-line="517" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1%20h-5%20bg-%5B%230ea5e9%5D%20rounded-full%20flex-shrink-0%22%7D" className="w-1 h-5 bg-[#0ea5e9] rounded-full flex-shrink-0" />
          <h2 data-component-id="src/app/article/[slug]/ArticleComments.tsx:518:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="518" data-component-end-line="526" data-component-file="ArticleComments.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-white%20font-black%20text-lg%20uppercase%20tracking-widest%22%2C%22textContent%22%3A%22Discussion%22%7D"
            className="text-white font-black text-lg uppercase tracking-widest"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.1em' }}
          >
            Discussion
            {totalCount > 0 && (
              <span data-component-id="src/app/article/[slug]/ArticleComments.tsx:524:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="524" data-component-end-line="524" data-component-file="ArticleComments.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-%5B%230ea5e9%5D%20text-base%20font-bold%22%2C%22textContent%22%3A%22(%20)%22%7D" className="ml-2 text-[#0ea5e9] text-base font-bold">({totalCount})</span>
            )}
          </h2>
        </div>

        {/* Sort */}
        {totalCount > 1 && (
          <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:531:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="531" data-component-end-line="545" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20bg-%5B%230d1420%5D%20border%20border-white%2F10%20rounded%20p-0.5%22%7D" className="flex items-center gap-1 bg-[#0d1420] border border-white/10 rounded p-0.5">
            {(['newest', 'top'] as const).map((opt) => (
              <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:533:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="533" data-component-end-line="543" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={opt}
                onClick={() => setSortBy(opt)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-colors capitalize ${
                  sortBy === opt
                    ? 'bg-[#0ea5e9] text-white'
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {opt === 'newest' ? 'Newest' : 'Top'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* New comment form */}
      <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:550:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="550" data-component-end-line="558" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-%5B%23131c2e%5D%20border%20border-white%2F10%20rounded-lg%20p-5%20mb-6%22%7D" className="bg-[#131c2e] border border-white/10 rounded-lg p-5 mb-6">
        <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:551:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="551" data-component-end-line="553" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F50%20text-xs%20uppercase%20tracking-widest%20font-semibold%20mb-3%22%2C%22textContent%22%3A%22Join%20the%20conversation%22%7D" className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-3">
          Join the conversation
        </p>
        <CommentForm data-component-id="src/app/article/[slug]/ArticleComments.tsx:554:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="554" data-component-end-line="557" data-component-file="ArticleComments.tsx" data-component-name="CommentForm" data-component-content="%7B%22elementName%22%3A%22CommentForm%22%7D"
          articleSlug={articleSlug}
          onSubmitted={fetchComments}
        />
      </div>

      {/* Comment list */}
      {loading ? (
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:562:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="562" data-component-end-line="573" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%20py-4%22%7D" className="space-y-4 py-4">
          {[1, 2, 3].map((i) => (
            <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:564:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="564" data-component-end-line="571" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%20animate-pulse%22%7D" key={i} className="flex gap-3 animate-pulse">
              <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:565:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="565" data-component-end-line="565" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20rounded-full%20bg-white%2F5%20flex-shrink-0%22%7D" className="w-8 h-8 rounded-full bg-white/5 flex-shrink-0" />
              <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:566:14" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="566" data-component-end-line="570" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20space-y-2%22%7D" className="flex-1 space-y-2">
                <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:567:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="567" data-component-end-line="567" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-3%20bg-white%2F5%20rounded%20w-32%22%7D" className="h-3 bg-white/5 rounded w-32" />
                <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:568:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="568" data-component-end-line="568" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-3%20bg-white%2F5%20rounded%20w-full%22%7D" className="h-3 bg-white/5 rounded w-full" />
                <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:569:16" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="569" data-component-end-line="569" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-3%20bg-white%2F5%20rounded%20w-3%2F4%22%7D" className="h-3 bg-white/5 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:575:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="575" data-component-end-line="583" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-6%20text-center%22%7D" className="py-6 text-center">
          <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:576:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="576" data-component-end-line="576" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-red-400%2F70%20text-sm%20mb-3%22%7D" className="text-red-400/70 text-sm mb-3">{error}</p>
          <button data-component-id="src/app/article/[slug]/ArticleComments.tsx:577:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="577" data-component-end-line="582" data-component-file="ArticleComments.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-%5B%230ea5e9%5D%20text-xs%20hover%3Aunderline%22%2C%22textContent%22%3A%22Try%20again%22%7D"
            onClick={fetchComments}
            className="text-[#0ea5e9] text-xs hover:underline"
          >
            Try again
          </button>
        </div>
      ) : comments.length === 0 ? (
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:585:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="585" data-component-end-line="587" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-8%20text-center%22%7D" className="py-8 text-center">
          <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:586:10" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="586" data-component-end-line="586" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F25%20text-sm%22%2C%22textContent%22%3A%22No%20comments%20yet.%20Be%20the%20first%20to%20start%20the%20discussion.%22%7D" className="text-white/25 text-sm">No comments yet. Be the first to start the discussion.</p>
        </div>
      ) : (
        <div data-component-id="src/app/article/[slug]/ArticleComments.tsx:589:8" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="589" data-component-end-line="603" data-component-file="ArticleComments.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22divide-y%20divide-white%2F5%22%7D" className="divide-y divide-white/5">
          {comments.map((comment) => (
            <CommentItem data-component-id="src/app/article/[slug]/ArticleComments.tsx:591:12" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="591" data-component-end-line="601" data-component-file="ArticleComments.tsx" data-component-name="CommentItem" data-component-content="%7B%22elementName%22%3A%22CommentItem%22%7D"
              key={comment.id}
              comment={comment}
              articleSlug={articleSlug}
              depth={0}
              upvotedSet={upvotedSet}
              flaggedSet={flaggedSet}
              onUpvote={handleUpvote}
              onFlag={handleFlag}
              onReplyPosted={fetchComments}
            />
          ))}
        </div>
      )}

      {/* Community guidelines note */}
      <p data-component-id="src/app/article/[slug]/ArticleComments.tsx:607:6" data-component-path="src/app/article/[slug]/ArticleComments.tsx" data-component-line="607" data-component-end-line="609" data-component-file="ArticleComments.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-6%20text-white%2F15%20text-%5B10px%5D%20leading-relaxed%22%2C%22textContent%22%3A%22Comments%20are%20moderated.%20Please%20keep%20discussion%20respectful%20and%20on-topic.%20Flag%20inappropriate%20content%20using%20the%20flag%20icon.%22%7D" className="mt-6 text-white/15 text-[10px] leading-relaxed">
        Comments are moderated. Please keep discussion respectful and on-topic. Flag inappropriate content using the flag icon.
      </p>
    </section>
  );
}
